"use client";

import React, { useEffect, useRef } from "react";
import type { TAmazonPayHeapMethod } from "@features/checkout/types";
import { AMAZON_PAY_KEY_CONFIG } from "@lib/utils/checkout-utility/amazon-pay/constants";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { waitForWindowGlobal } from "@lib/utils/waitForWindowGlobal";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { CheckoutProgressSubtypes } from "@lib/analytics/analyticsTypes";

type HeapAmazonPayButtonProps = {
    method: TAmazonPayHeapMethod;
    width?: string;
};

type AmazonPaySdk = NonNullable<NonNullable<Window["amazon"]>["Pay"]>;

const MOUNT_ID = "amazon-pay-button-heap-mount";
const SDK_TIMEOUT_MS = 10000;

export const HeapAmazonPayButton = ({ method, width = "250px" }: HeapAmazonPayButtonProps) => {
    const mountRef = useRef<HTMLDivElement | null>(null);
    const { merchantId, currency, checkoutLanguage, payloadJSON, signature, publicKeyId } = method;

    useEffect(() => {
        let cancelled = false;

        const render = async () => {
            let sdk: AmazonPaySdk;
            try {
                sdk = await waitForWindowGlobal<AmazonPaySdk>(() => window.amazon?.Pay, {
                    timeoutMs: SDK_TIMEOUT_MS,
                    name: "Amazon Pay SDK",
                });
            } catch (error) {
                Logger.error(ServiceType.AMAZON_PAY, "Failed to load Amazon Checkout.js (HEAP)", {
                    tag: LogTag.CHECKOUT,
                    error,
                });
                return;
            }
            if (cancelled || !mountRef.current) return;

            // Decoupled flow: Amazon renders the button in its own iframe and relays clicks over
            // postMessage, so a DOM listener never sees them. Omitting createCheckoutSessionConfig
            // here is what makes the SDK hand back a button exposing onClick/initCheckout.
            const button = sdk.renderButton(`#${MOUNT_ID}`, {
                merchantId,
                publicKeyId,
                ledgerCurrency: currency,
                checkoutLanguage,
                productType: "PayAndShip",
                placement: "Checkout",
                buttonColor: "DarkGray",
            });

            if (!button?.onClick) {
                Logger.error(
                    ServiceType.AMAZON_PAY,
                    "Amazon Pay button did not expose onClick (HEAP)",
                    { tag: LogTag.CHECKOUT }
                );
                return;
            }

            button.onClick(() => {
                // Amazon Pay sits outside the Adyen dropin, so its onSelect/onSubmit never run
                // for it. This click is both the method selection and the gateway handoff.
                try {
                    AnalyticsClient.checkoutProgress(
                        CheckoutProgressSubtypes.paymentMethodSelected,
                        "AmazonPay",
                        "user"
                    );
                    AnalyticsClient.checkoutProgress(CheckoutProgressSubtypes.goToPaymentGateway);
                } catch (error) {
                    Logger.error(ServiceType.AMAZON_PAY, "Amazon Pay click analytics failed", {
                        tag: LogTag.CHECKOUT,
                        error,
                    });
                }

                // Must run even if analytics threw — this is what starts the payment.
                button.initCheckout({
                    createCheckoutSessionConfig: {
                        // payloadJSON is signed by HEAP — pass verbatim, do not parse/re-serialize.
                        payloadJSON,
                        signature,
                        algorithm: AMAZON_PAY_KEY_CONFIG.algorithm,
                        publicKeyId,
                    },
                });
            });
        };

        render();

        return () => {
            cancelled = true;
        };
    }, [merchantId, currency, checkoutLanguage, payloadJSON, signature, publicKeyId]);

    return (
        <div style={{ width, maxWidth: "100%" }}>
            {/* key={signature}: when the HEAP-signed payload changes, React unmounts
                the previous div (with Amazon's injected button) and mounts a fresh one,
                so Amazon's renderButton never sees a re-used node. */}
            <div key={signature} id={MOUNT_ID} ref={mountRef} />
        </div>
    );
};
